// Canonical profit math for the entire app.
//
// Cash-basis: filament is recorded as an expense at purchase time (when a spool
// is bought). Therefore a print's filament cost is NOT subtracted from sales
// profit — doing so would double-count. The only print costs that affect
// profit are machine wear + electricity + labor (= machine_cost + labor_cost,
// since electricity is folded into machine_cost by pricing.ts).
//
// Refunds: a sale flagged is_refunded contributes $0 to income (the money went
// back to the customer). It is reported separately so the dashboards can show
// "$X refunded this period."
//
// Mom's cut: for sales tagged with a consignee_id (Part B Mom Mode), the
// moms_cut_at_sale field is a snapshot of what mom earned on that sale. That
// amount is subtracted from profit because mom keeps it. Until Part B lands,
// no sales have moms_cut_at_sale set, so the subtraction is always $0.

import type { Sale, Expense, Print } from './database.types'

// Schema may evolve; tolerate the field being absent for sales rows that
// pre-date Part B's `moms_cut_at_sale` column.
type SaleWithMaybeMomCut = Sale & { moms_cut_at_sale?: number | null }

export interface ProfitBreakdown {
  /** Net revenue from non-refunded sales (gross + shipping_charged − fees − shipping_cost). */
  income: number
  /** All expense rows in the period. */
  expensesOut: number
  /** Sum of machine_cost + labor_cost from prints in the period. Filament is excluded (already an expense). */
  printCostsOut: number
  /** Sum of moms_cut_at_sale on non-refunded sales. Zero until Mom Mode logs sales. */
  momsCutOut: number
  /** Bottom line: income − expensesOut − printCostsOut − momsCutOut. */
  profit: number
  /** Number of refunded sales in the period (for display, not profit math). */
  refundedCount: number
  /** Net revenue that would have come from refunded sales (for "$X refunded" display). */
  refundedRevenue: number
}

/**
 * Compute profit + breakdown for a pre-filtered period.
 *
 * The caller is responsible for filtering sales/expenses/prints to the
 * desired date range. This function only does the math.
 */
export function computeProfitForPeriod(input: {
  sales: SaleWithMaybeMomCut[]
  expenses: Pick<Expense, 'amount'>[]
  prints: Pick<Print, 'machine_cost' | 'labor_cost'>[]
}): ProfitBreakdown {
  let income = 0
  let momsCutOut = 0
  let refundedCount = 0
  let refundedRevenue = 0

  for (const s of input.sales) {
    const net = saleNetRevenue(s)
    if (s.is_refunded) {
      refundedCount += 1
      refundedRevenue += net
      continue
    }
    income += net
    momsCutOut += Number(s.moms_cut_at_sale ?? 0)
  }

  const expensesOut = input.expenses.reduce((sum, e) => sum + Number(e.amount), 0)
  const printCostsOut = input.prints.reduce(
    (sum, p) => sum + Number(p.machine_cost) + Number(p.labor_cost),
    0,
  )

  const profit = income - expensesOut - printCostsOut - momsCutOut

  return {
    income,
    expensesOut,
    printCostsOut,
    momsCutOut,
    profit,
    refundedCount,
    refundedRevenue,
  }
}

/**
 * Net revenue from a single sale (what you actually take home, before costs).
 *
 * Mirrors the function in useSales.ts so callers don't need to import both.
 * (The hook re-exports its own copy for backward compatibility.)
 */
export function saleNetRevenue(s: Pick<Sale, 'gross_amount' | 'shipping_charged' | 'platform_fees' | 'shipping_cost'>): number {
  return (
    Number(s.gross_amount) +
    Number(s.shipping_charged) -
    Number(s.platform_fees) -
    Number(s.shipping_cost)
  )
}
